import {
  Control,
  FieldErrors,
  FieldValues,
  FormState,
  Path,
  PathValue,
  UseFormSetValue,
} from 'react-hook-form'
import { View } from 'react-native'
import { StyleSheet } from 'react-native-unistyles'

import FormFieldCheckbox from '@/components/form/FormFieldCheckbox'
import FormFieldDate from '@/components/form/FormFieldDate'
import FormFieldNumber from '@/components/form/FormFieldNumber'
import FormFieldText from '@/components/form/FormFieldText'
import FormFieldMedicationName from '@/components/fosters/cards/formFields/FormFieldMedicationName'
import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'
import Species from '@/enums/species'
import UserService from '@/services/userService'

interface Props<C extends FieldValues, _E extends FieldErrors> {
  pathPrefix: string
  control: Control<C, any>
  disabled: boolean
  formState: FormState<C>
  medication?: any
  species: Species | undefined
  setValue: UseFormSetValue<C>
}

const FosterMedicationForm = <C extends FieldValues, E extends FieldErrors>({
  pathPrefix,
  control,
  disabled,
  formState,
  medication,
  species,
  setValue,
}: Props<C, E>) => {
  const { user } = UserService.useCurrentUser()

  const markMedicationAsGiven = () => {
    setValue(
      `${pathPrefix}.givenDate` as Path<C>,
      new Date() as PathValue<C, Path<C>>,
      {
        shouldDirty: true,
      }
    )
    setValue(
      `${pathPrefix}.givenBy` as Path<C>,
      `${user?.firstName || ''} ${user?.lastName || ''}` as PathValue<
        C,
        Path<C>
      >,
      { shouldDirty: true }
    )
  }

  return (
    <>
      <FormFieldMedicationName
        control={control}
        disabled={disabled}
        fieldName={`${pathPrefix}.name` as Path<C>}
        formState={formState}
        label="Name"
        required={true}
        species={species}
        style={styles.formField}
      />
      {!medication?.givenDate && (
        <>
          <FormFieldDate
            control={control}
            disabled={disabled}
            errors={formState.errors}
            fieldName={`${pathPrefix}.dueDate` as Path<C>}
            label="Due On"
            required={false}
            style={styles.formField}
          />
        </>
      )}
      {!medication?.givenDate && (
        <UniButton onPress={markMedicationAsGiven}>
          I just gave this medication
        </UniButton>
      )}
      <FormFieldDate
        control={control}
        disabled={disabled}
        errors={formState.errors}
        fieldName={`${pathPrefix}.givenDate` as Path<C>}
        label="Given On"
        required={false}
        style={styles.formField}
      />
      <FormFieldText
        control={control}
        disabled={disabled}
        errors={formState.errors}
        fieldName={`${pathPrefix}.givenBy` as Path<C>}
        label="Given By"
        required={true}
        style={styles.formField}
      />
      <FormFieldText
        control={control}
        disabled={disabled}
        errors={formState.errors}
        fieldName={`${pathPrefix}.notes` as Path<C>}
        label="Notes"
        multiline
        numberOfLines={3}
        required={false}
        style={styles.formField}
      />
      <FormFieldCheckbox
        control={control}
        disabled={disabled}
        fieldName={`${pathPrefix}.isRepeating` as Path<C>}
        label="Repeat this medication?"
        required={false}
        status="unchecked"
      />
      {medication?.isRepeating && (
        <View style={styles.repeatContainer}>
          <UniText>Repeat this medication every </UniText>
          <FormFieldNumber
            aria-label="Frequency in days"
            control={control}
            disabled={disabled}
            errors={formState.errors}
            fieldName={`${pathPrefix}.frequency` as Path<C>}
            required={false}
            style={styles.frequency}
          />
          <UniText> days</UniText>
        </View>
      )}
    </>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    formField: {
      flex: 1,
    },
    frequency: {
      marginLeft: theme.tokens.spacing[2],
      marginRight: theme.tokens.spacing[2],
      width: 60,
    },
    repeatContainer: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
    },
  }
})

export default FosterMedicationForm
